import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useViews } from "@/hooks/useViews";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { captureError } from '@/lib/sentry';

const filterSchema = z.object({
  name: z.string().min(1, "Filter name is required"),
});

interface SaveFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentFilters: any;
}

export function SaveFilterDialog({
  open,
  onOpenChange,
  currentFilters,
}: SaveFilterDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { saveView } = useViews();
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof filterSchema>>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof filterSchema>) => {
    setIsLoading(true);
    try {
      // Make sure we preserve the top-level group_type
      const filtersToSave = {
        ...currentFilters,
        group_type: currentFilters.group_type || 'AND'
      };

      const view = await saveView(values.name, filtersToSave);
      
      toast({
        description: "Filter saved successfully",
      });
      
      form.reset();
      onOpenChange(false);
      
      // Navigate to the new filter view
      if (view) {
        navigate(`/filters/${view.id}`);
      }
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-save-filter' });
      toast({
        title: "Error",
        description: "Failed to save filter",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Save Filter</DialogTitle>
          <DialogDescription>
            Give your filter a name to save it for later use.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Filter Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter filter name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save filter"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}